import * as React from 'react';
import { registerSessionQueryClient } from '@/lib/session-query-cache';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetCurrentUserQueryKey,
  setViewAsRoleProvider,
  useGetCurrentUser,
  type CurrentUserGroupAccess,
  type ActiveElevation,
  type ActiveOverride,
  type TenantMembership
} from '@cdf/api-client-react';
import { TENANT_PERMISSION_KEYS } from '@/features/tenancy/permissions';
import { tenantSlugFromPath } from '@/features/tenancy/urls';
import { useAuth } from '@/hooks/use-auth';
import {
  canUseRoleSwitcher,
  canViewAsRole,
  demoteToProductRole,
  isProductRoleCode,
  listViewAsRoles,
  type ProductRoleCode
} from '@/lib/product-roles';
import {
  clearViewAsRole,
  getViewAsRole,
  setViewAsRole,
  subscribeViewAsRole
} from '@/lib/role-view';

/**
 * Session bridge.
 * Adapts the MSAL-backed `useAuth` contract to the simple Session/User
 * surface consumed by layout (AppShell, ProfileMenu, RoleGate).
 *
 * UI AppRole + capabilities come from GET /api/auth/me (DB-derived product
 * roles), not MSAL token claims. Fail closed to `viewer` if /me is unavailable.
 *
 * High-auth principals may temporarily demote via `viewAsRole` (session-only);
 * API requests re-enforce demotion with X-View-As-Role so it is not cosmetic.
 */

export type UserRole = 'viewer' | 'editor' | 'admin';

export type SessionStatus = 'loading' | 'signedIn' | 'signedOut' | 'accessError';

export interface SessionUser {
  name: string;
  email: string;
  initials: string;
  /** Effective UI AppRole after optional view-as demotion. */
  role: UserRole;
  /** Effective capabilities after optional view-as demotion. */
  capabilities: string[];
  /** Effective product roles after optional view-as demotion. */
  productRoles: string[];
  /** Assigned product roles from the server (pre-demotion). */
  realProductRoles: string[];
  /** Active demoted product role when using “View as”, else null. */
  viewAsRole: ProductRoleCode | null;
  appUserId: string | null;
  isPlatformAdmin: boolean;
  tenants: TenantMembership[];
  groupAccess?: CurrentUserGroupAccess;
  activeElevations: ActiveElevation[];
  activeOverrides: ActiveOverride[];
  avatarUrl?: string;
}

export type Session =
  | { status: 'loading' }
  | { status: 'signedOut' }
  | { status: 'accessError'; forbidden: boolean; groupUnavailable?: boolean }
  | { status: 'signedIn'; user: SessionUser };

interface SessionContextValue {
  session: Session;
  signIn: () => void;
  signOut: () => void;
  retryAccess: () => void;
  refreshUser: (throwOnError?: boolean) => Promise<void>;
  /** Product roles the signed-in high-auth user may demote into. */
  viewAsOptions: ProductRoleCode[];
  setViewAs: (role: ProductRoleCode | null) => void;
  clearViewAs: () => void;
}

const SessionContext = React.createContext<SessionContextValue | null>(null);

function initialsFrom(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0]!}${parts[1]![0]!}`.toUpperCase();
  }
  if (parts.length === 1 && parts[0]!.length >= 2) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (email.slice(0, 2) || 'U').toUpperCase();
}

function coerceRole(role: string | undefined): UserRole {
  if (role === 'admin' || role === 'editor' || role === 'viewer') return role;
  return 'viewer';
}

const LOCAL_DEVELOPER: SessionUser = {
  name: 'Local Developer',
  email: 'dev@localhost',
  initials: 'LD',
  role: 'admin',
  capabilities: [
    'booking:self',
    'booking:force_cancel',
    'inventory:manage',
    'policy:manage',
    'reports:view',
    'audit:view',
    'integrations:manage',
    'ai:booking',
    'ai:admin_insights',
    'visitors:host',
    'visitors:desk',
    'visitors:admin'
  ],
  productRoles: ['it_admin'],
  realProductRoles: ['it_admin'],
  viewAsRole: null,
  appUserId: null,
  isPlatformAdmin: true,
  tenants: [
    {
      id: 'local-default',
      slug: 'default',
      baseSlug: '00000000-0000-4000-8000-000000000001',
      alias: 'default',
      name: 'Default entity',
      tenantId: 'local-tenant',
      tenantName: 'Default tenant',
      status: 'active',
      roles: [{ id: 'local-owner', key: 'owner', name: 'Owner', isSystem: true }],
      permissions: [...TENANT_PERMISSION_KEYS]
    }
  ],
  activeElevations: [],
  activeOverrides: []
};

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { enabled, ready, isAuthenticated, account, accountKey, login, logout, tokenError } = useAuth();
  const { pathname } = useLocation();
  const tenantSlug = tenantSlugFromPath(pathname);
  const [viewAsRole, setViewAsRoleState] = React.useState<ProductRoleCode | null>(
    () => getViewAsRole()
  );

  const queryClient = useQueryClient();
  React.useLayoutEffect(() => registerSessionQueryClient(queryClient), [queryClient]);
  React.useEffect(() => subscribeViewAsRole(role => {
    // A previous real/demoted session must be reverified when returning to it.
    queryClient.removeQueries({ queryKey: getGetCurrentUserQueryKey(), type: 'inactive' });
    setViewAsRoleState(role);
  }), [queryClient]);

  React.useLayoutEffect(() => {
    setViewAsRoleProvider(() => getViewAsRole());
    return () => setViewAsRoleProvider(null);
  }, []);

  // Product capabilities depend on the selected entity; never reuse an
  // unscoped response or another account's grants after navigation.
  const meQuery = useGetCurrentUser({
    request: { headers: { 'X-Tenant-Slug': tenantSlug ?? '' } },
    query: {
      queryKey: [...getGetCurrentUserQueryKey(), accountKey, tenantSlug, viewAsRole],
      enabled: enabled && ready && isAuthenticated && Boolean(accountKey),
      // Failed authorization needs a deliberate recovery action, not retries.
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: 60_000,
      // Pick up grants made by another administrator without repeating failed authorization.
      refetchInterval: (query) => query.state.status === 'error' ? false : 60_000,
      refetchIntervalInBackground: false
    }
  });

  const realProductRoles = React.useMemo(() => {
    if (!enabled) return LOCAL_DEVELOPER.realProductRoles;
    return meQuery.data?.productRoles ?? [];
  }, [enabled, meQuery.data?.productRoles]);

  // Mirrors the server: any organisation permission in the current, active
  // organisation allows an Employee preview whatever the product role.
  const canDemoteOrganisation = Boolean(tenantSlug && meQuery.data?.tenants.some(tenant =>
    tenant.slug === tenantSlug && tenant.status === 'active' && tenant.permissions.length > 0));
  const canSelectView = React.useCallback((role: ProductRoleCode) => canViewAsRole(realProductRoles, role) ||
    (role === 'employee' && canDemoteOrganisation), [realProductRoles, canDemoteOrganisation]);

  // Drop a stale view-as when the principal no longer qualifies for it.
  React.useEffect(() => {
    if (!viewAsRole || (enabled && !meQuery.data)) return;
    if (!canSelectView(viewAsRole)) {
      clearViewAsRole();
    }
  }, [enabled, meQuery.data, canSelectView, viewAsRole]);

  const session = React.useMemo<Session>(() => {
    if (!enabled) {
      if (viewAsRole && canViewAsRole(LOCAL_DEVELOPER.realProductRoles, viewAsRole)) {
        const demoted = demoteToProductRole(viewAsRole);
        return {
          status: 'signedIn',
          user: {
            ...LOCAL_DEVELOPER,
            role: demoted.role,
            capabilities: demoted.capabilities,
            productRoles: demoted.productRoles,
            viewAsRole
          }
        };
      }
      return { status: 'signedIn', user: { ...LOCAL_DEVELOPER, viewAsRole: null } };
    }
    if (!isAuthenticated || !account || !accountKey) {
      return { status: ready ? 'signedOut' : 'loading' };
    }
    // An established, scoped session stays mounted during token acquisition.
    // Pending initial/account/entity loads still block, and failures below
    // revoke access rather than retaining stale authority.
    if (!ready && !meQuery.data) {
      return { status: 'loading' };
    }

    if (tokenError) return { status: 'accessError', forbidden: false };

    // Wait for first /me so role gating does not flash admin/unrestricted.
    if (meQuery.isPending || (meQuery.isFetching && !meQuery.data)) {
      return { status: 'loading' };
    }

    // Auth token failure must not look "signed in" — that races protected pages
    // into API calls that surface "Token validation failed" with empty roles.
    if (meQuery.isError) {
      const status =
        typeof meQuery.error === 'object' &&
        meQuery.error !== null &&
        'status' in meQuery.error &&
        typeof (meQuery.error as { status?: unknown }).status === 'number'
          ? (meQuery.error as { status: number }).status
          : undefined;
      const groupUnavailable = meQuery.error && typeof meQuery.error === 'object' && 'code' in meQuery.error &&
        meQuery.error.code === 'GROUP_ACCESS_UNAVAILABLE';
      return { status: 'accessError', forbidden: status === 403, groupUnavailable: Boolean(groupUnavailable) };
    }

    const name =
      meQuery.data?.displayName || account.name || account.email || 'User';
    const email = meQuery.data?.email || account.email || '';
    const baseRoles = meQuery.data?.productRoles ?? [];
    const baseCaps = meQuery.data?.capabilities ?? [];
    // This admits navigation only. Report APIs independently recheck current
    // destination-scoped authority; it never grants an entity-wide API role.
    const navigationCaps = meQuery.data?.workplaceReportNavigation
      ? [...new Set([...baseCaps, 'reports:view'])] : baseCaps;
    const baseRole = coerceRole(meQuery.data?.role);

    const effectiveViewAs =
      viewAsRole && canSelectView(viewAsRole) ? viewAsRole : null;
    const demoted = effectiveViewAs ? demoteToProductRole(effectiveViewAs) : null;

    return {
      status: 'signedIn',
      user: {
        name,
        email,
        initials: initialsFrom(name, email),
        role: demoted?.role ?? baseRole,
        // The server computes report navigation under the same validated preview.
        capabilities: demoted
          ? (meQuery.data?.workplaceReportNavigation
            ? [...new Set([...demoted.capabilities, 'reports:view'])] : demoted.capabilities)
          : navigationCaps,
        productRoles: demoted?.productRoles ?? baseRoles,
        realProductRoles: baseRoles,
        viewAsRole: effectiveViewAs,
        appUserId: meQuery.data?.appUserId ?? null,
        isPlatformAdmin: effectiveViewAs ? false : meQuery.data?.isPlatformAdmin ?? false,
        tenants: (meQuery.data?.tenants ?? []).map(tenant => effectiveViewAs ? { ...tenant, permissions: [] } : tenant),
        groupAccess: meQuery.data?.groupAccess,
        activeElevations: meQuery.data?.activeElevations ?? [],
        activeOverrides: meQuery.data?.activeOverrides ?? []
      }
    };
  }, [
    enabled,
    ready,
    isAuthenticated,
    account,
    accountKey,
    meQuery.isPending,
    meQuery.isFetching,
    meQuery.isError,
    meQuery.error,
    meQuery.data,
    tokenError,
    viewAsRole,
    canSelectView
  ]);

  const viewAsOptions = React.useMemo(
    () => [...new Set([...listViewAsRoles(realProductRoles), ...(canDemoteOrganisation ? ['employee' as const] : [])])],
    [realProductRoles, canDemoteOrganisation]
  );

  const handleSetViewAs = React.useCallback(
    (role: ProductRoleCode | null) => {
      if (role === null) {
        clearViewAsRole();
        return;
      }
      if (!canSelectView(role)) return;
      setViewAsRole(role);
    },
    [canSelectView]
  );

  const handleSignOut = React.useCallback(() => {
    clearViewAsRole();
    logout();
  }, [logout]);

  // /me belongs to this outer query provider, not the entity-scoped client
  // used by the app shell. Always retry its own observer.
  const retryAccess = React.useCallback(() => { void meQuery.refetch(); }, [meQuery.refetch]);
  const refreshUser = React.useCallback(async (throwOnError = false) => {
    const result = await meQuery.refetch();
    if (throwOnError && result.error) throw result.error;
  }, [meQuery.refetch]);

  const value = React.useMemo<SessionContextValue>(
    () => ({
      session,
      signIn: login,
      signOut: handleSignOut,
      retryAccess,
      refreshUser,
      viewAsOptions: session.status === 'signedIn' ? viewAsOptions : [],
      setViewAs: handleSetViewAs,
      clearViewAs: clearViewAsRole
    }),
    [session, login, handleSignOut, retryAccess, refreshUser, viewAsOptions, handleSetViewAs]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = React.useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within UserProvider');
  }
  return ctx;
}

/** Convenience helper for signed-in surfaces. */
export function useUser(): SessionUser | null {
  const { session } = useSession();
  return session.status === 'signedIn' ? session.user : null;
}

/** True when the session user holds a product capability code (effective). */
export function useHasCapability(capability: string): boolean {
  const user = useUser();
  return Boolean(user?.capabilities.includes(capability));
}

/** True when the real principal may open the role switcher. */
export function useCanUseRoleSwitcher(): boolean {
  const { session, viewAsOptions } = useSession();
  const user = session.status === 'signedIn' ? session.user : null;
  return canUseRoleSwitcher(user?.realProductRoles ?? []) || Boolean(user?.viewAsRole) || viewAsOptions.length > 0;
}

export { isProductRoleCode };
export type { ProductRoleCode };
